import { useCallback, useState } from 'react';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import { useQueryClient } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';

import type { FileRejections, ZDocsUpload } from '@zillow/zdocs-web';
import {
  errorMessageFromCode,
  FileManager,
  FileWorkflowManager,
  RejectionErrorCode,
  ZDocsUploader,
} from '@zillow/zdocs-web';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import {
  useReevaluateLeaseCompletionMutation,
  useStoreDocumentIdMutation,
  useUploadedFileDetailsQuery,
  useZDocsAccessTokenQuery,
} from '../utils/queries';
import { MAXIMUM_NUMBER_OF_DOCUMENTS } from '../pages/LandlordLeaseView/SelfServeView/UploadedLease';
import { Toast, useToast } from '@zillow/constellation-10';

const MULTI_FILE_UPLOAD_ACCEPTED_FILES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'image/heic',
];

export enum UPLOAD_ERRORS {
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  TOO_MANY_FILES = 'TOO_MANY_FILES',
}

type Props = {
  isSignedSelfUpload?: boolean;
  onFilesUploading?: (count: number) => void;
};

function LeaseUploader({ isSignedSelfUpload, onFilesUploading }: Props) {
  const { leaseId, propertyId } = useLeasingUrlParams();
  const queryClient = useQueryClient();

  const [messageTokens, setMessageTokens] = useState([]);
  const { enqueueToast } = useToast();

  const { data: uploadedFileDetails = [] } = useUploadedFileDetailsQuery(Number(leaseId));
  const maximumNumberOfFiles = MAXIMUM_NUMBER_OF_DOCUMENTS - uploadedFileDetails.length;

  const onErrorToast = useCallback(
    (message: string) => {
      enqueueToast(<Toast tone="critical">{message}</Toast>);
    },
    [enqueueToast],
  );

  const uploadTokenQuery = useZDocsAccessTokenQuery(Number(leaseId));
  const storeDocumentIdMutation = useStoreDocumentIdMutation();
  const reevaluateLeaseCompletionMutation = useReevaluateLeaseCompletionMutation();

  async function onUploadComplete(upload: ZDocsUpload) {
    messageTokens.push(upload.messageToken);
    setMessageTokens(messageTokens);
  }

  function onUploadFailed() {
    onErrorToast('Make sure your file is a image, PDF, or Word document');
  }

  async function onUploadActive(active: boolean) {
    if (!active) {
      await storeDocumentIdMutation.mutateAsync({
        postData: {
          listingAlias: propertyId,
          messageTokens: messageTokens,
          leaseId: Number(leaseId),
          isSignedSelfUpload: isSignedSelfUpload,
        },
      });

      try {
        await reevaluateLeaseCompletionMutation.mutateAsync({
          postData: {
            leaseArtifactType: LeaseArtifactType.LEASE,
            leaseArtifactId: Number(leaseId),
          },
        });

        await queryClient.invalidateQueries({
          predicate: (query) => query.meta?.tags?.includes('leasing:lease-status-change'),
        });
      } catch (error) {
        logError({
          error,
          context: { details: 'Failed to reevaluate lease completion after document upload', leaseId },
        });
      }
    }
  }

  const generateUploadToken = async () => {
    return uploadTokenQuery.data.token || '';
  };

  function onFileRejected(rejections: FileRejections) {
    const errorMessage = errorMessageFromCode(rejections.rejections[0]?.errorCodes[0] || RejectionErrorCode.Unknown);
    onErrorToast(errorMessage);
  }

  return (
    <ZDocsUploader
      key={maximumNumberOfFiles}
      generateUploadToken={generateUploadToken}
      onUploadComplete={onUploadComplete}
      onUploadActive={onUploadActive}
      onUploadFailed={onUploadFailed}
      onFileRejected={onFileRejected}
      placeholderText={`You can upload up to ${MAXIMUM_NUMBER_OF_DOCUMENTS} images, PDFs, or Word documents. Each file must be smaller than 100MB.`}
      accept={MULTI_FILE_UPLOAD_ACCEPTED_FILES}
      maxFiles={maximumNumberOfFiles}
      /* eslint-disable react/prop-types */
      renderer={(props) => {
        const { pendingFiles, processingFiles } = props;
        const uploadingCount = pendingFiles.length + processingFiles.length;
        /* eslint-enable react/prop-types */

        if (onFilesUploading) {
          onFilesUploading(uploadingCount);
        }

        return (
          <>
            <FileManager {...props} />
            <FileWorkflowManager {...props} />
          </>
        );
      }}
    />
  );
}

export default LeaseUploader;
